import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    crudStatusEnum,
    getCrudStatusLabel,
    getPriceTypeLabel,
    localStorageKeys,
    permissionLevelEnum,
    systemConfigKeyEnum,
} from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateVehicleTypeMutation } from '../../../../hooks/mutations/useVehicleTypeMutation';
import { useVehicleTypesQuery } from '../../../../hooks/queries/useVehiclesTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { useTheme } from '../../../../hooks/useTheme';
import { VehicleType } from '../../../../services/apiSLTP/materials/vehiclesTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatNumber } from '../../../../utils/numbers';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateVehicleTypeModal from '../../../modals/admin/vehicles/UpdateVehicleTypeModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface VehiclesTypesTableProps {
    withUsageCount: boolean;
}

const VehiclesTypesTable: React.FC<VehiclesTypesTableProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();
    const { muiTheme } = useTheme();

    const [selected, setSelected] = useState<VehicleType | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data, isLoading, refetch } = useVehicleTypesQuery({
        queries: { withUsage: withUsageCount },
    });

    const { mutate: mutateVehicleType } = useUpdateVehicleTypeMutation({
        onSuccess: () => refetch(),
    });

    const columns: ColDef<VehicleType>[] = useMemo(() => {
        const base: ColDef<VehicleType>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                initialFlex: 1,
                minWidth: 400,
                colId: 'name',
            },

            {
                headerName: capitalizeFirstLetter(t('actions')),
                cellRenderer: (params: ICellRendererParams<VehicleType>) => {
                    if (!params.data) return null;
                    const disabled = params.data?.crudStatus === crudStatusEnum.DELETED;
                    return (
                        <IconLine>
                            <IconButton
                                iconName="edit"
                                title={capitalizeFirstLetter(t('modifyVehicleType'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelected(params.data);
                                    setIsEditMode(true);
                                    setEditOpen(true);
                                }}
                                disabled={disabled}
                            />
                            {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                                <IconButton
                                    iconName="delete"
                                    title={capitalizeFirstLetter(t('deleteVehicleType'))}
                                    onClick={() => {
                                        if (!params.data) return;
                                        setSelected(params.data);
                                        setIsEditMode(false);
                                        setDeleteOpen(true);
                                    }}
                                    disabled={disabled}
                                />
                            )}
                        </IconLine>
                    );
                },
                colId: 'actions',
                filter: false,
                enableRowGroup: false,
                enableValue: false,
                sortable: false,
            },
            {
                headerName: capitalizeFirstLetter(t('status')),
                valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
                cellRenderer: (params: ICellRendererParams<VehicleType>) => {
                    if (!params.data) return params.value;
                    return (
                        <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />
                    );
                },
                colId: 'status',
            },
            {
                headerName: capitalizeFirstLetter(t('price')) + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
                valueFormatter: (params) => formatNumber(params.value, t('locale')),
                field: 'price',
                cellStyle: (params) => (params.data?.price === 0 ? { color: muiTheme.palette.error.main } : undefined),
                colId: 'price',
                type: 'numericColumn',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('pricing')),
                valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeLabel(params.data?.priceType))),
                colId: 'priceType',
            },
            {
                headerName: capitalizeFirstLetter(t('displayOrder')),
                field: 'displayOrder',
                colId: 'displayOrder',
                type: 'numericColumn',
                filter: 'agNumberColumnFilter',
            },
            createDateTimeColumn<VehicleType>('createdAt', 'createdAt'),
            createDateTimeColumn<VehicleType>('updatedAt', 'updatedAt'),
        ];

        if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
            base.push(
                {
                    headerName: '_id',
                    field: '_id',
                    cellClass: 'usage-col-main',
                    headerClass: 'usage-col-main',
                    initialWidth: 250,
                    colId: '_id',
                },
                {
                    headerName: t('xTotal'),
                    field: 'usageCount.total',
                    cellClass: 'usage-col',
                    headerClass: 'usage-col',
                    colId: 'xtotal',
                    filter: 'agNumberColumnFilter',
                },
                {
                    headerName: t('xVehicles'),
                    field: 'usageCount.vehicles',
                    cellClass: 'usage-col',
                    headerClass: 'usage-col',
                    colId: 'xvehicles',
                    filter: 'agNumberColumnFilter',
                }
            );
        }

        return base;
    }, [t, user]);

    return (
        <>
            <GenericAgGridTable
                rowData={(data || []).filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                columnDefs={columns}
                loading={isLoading}
                exportCsvFileName="admin-vehicleTypes"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelected(null);
                            setIsEditMode(false);
                            setEditOpen(true);
                        }}
                        title={capitalizeFirstLetter(t('addVehicleType'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.VEHICLES.VEHICLETYPES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {editOpen && (
                <UpdateVehicleTypeModal
                    showEditModal={editOpen}
                    setShowEditModal={setEditOpen}
                    vehicleType={selected}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {deleteOpen && (
                <DeleteModal
                    open={deleteOpen}
                    handleClose={() => setDeleteOpen(false)}
                    deleteAction={() => {
                        if (selected) {
                            mutateVehicleType({ vehicleTypeId: selected._id, data: { crudStatus: crudStatusEnum.DELETED } });
                        }
                    }}
                    title={t('vehicle')}
                    itemName={selected?.designation}
                />
            )}
        </>
    );
};

export default VehiclesTypesTable;
